import { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import { professionalsApi } from "../../../services";
import { Professional } from "../../../types";

export function useAdminReportsProfessional() {
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const isBarbershop = company?.category === "barbershop";
  const showProfessionalSelector = isBarbershop && !isProfessional;

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<number | null>(() => {
    const saved = localStorage.getItem("adminDashboardSelectedProfessionalId");
    return saved ? parseInt(saved, 10) : null;
  });
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);

  const professionalIdForApi = isProfessional ? undefined : selectedProfessionalId;

  useEffect(() => {
    if (selectedProfessionalId !== null) {
      localStorage.setItem("adminDashboardSelectedProfessionalId", selectedProfessionalId.toString());
    } else {
      localStorage.removeItem("adminDashboardSelectedProfessionalId");
    }
  }, [selectedProfessionalId]);

  useEffect(() => {
    const fetchProfessionals = async () => {
      if (!showProfessionalSelector || !company?.id) return;

      setLoadingProfessionals(true);
      try {
        const response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          const activeProfessionals = response.data.filter((p) => p.isActive !== false);
          setProfessionals(activeProfessionals);

          const savedProfessionalId = localStorage.getItem("adminDashboardSelectedProfessionalId");
          const savedId = savedProfessionalId ? parseInt(savedProfessionalId, 10) : null;

          if (savedId && activeProfessionals.some((p) => p.id === savedId)) {
            setSelectedProfessionalId(savedId);
          } else if (activeProfessionals.length > 0) {
            setSelectedProfessionalId(activeProfessionals[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch professionals:", error);
      } finally {
        setLoadingProfessionals(false);
      }
    };

    fetchProfessionals();
  }, [showProfessionalSelector, company?.id]);

  return {
    professionalIdForApi,
    professionalIdForDailyBalance: professionalIdForApi ?? null,
    showProfessionalSelector,
    professionals,
    selectedProfessionalId,
    setSelectedProfessionalId,
    loadingProfessionals,
  };
}
